import type { ChildInsights, DayRollup } from '@baby-tracker/contracts';
import type { VolumeDisplayUnit } from '@baby-tracker/validation';
import { lazy, Suspense, type ReactElement } from 'react';

import { formatDuration, formatVolume } from '../../../lib/activity-format.js';
import styles from './insights.module.css';

const TrendChart = lazy(() =>
    import('../../charts/TrendChart.js').then((module) => ({ default: module.TrendChart })),
);

export function TrendCards({
    insights,
    unit,
    days,
    childId,
}: {
    insights: ChildInsights;
    unit: VolumeDisplayUnit;
    days: DayRollup[] | undefined;
    childId: string;
}): ReactElement {
    const empty =
        insights.feedingCount === 0 && insights.sleepSeconds === 0 && insights.nappyCount === 0;
    const series = days ?? [];
    return (
        <section aria-label="Trends">
            {empty ? (
                <p className={styles.meta}>Nothing recorded in these {insights.days} days.</p>
            ) : (
                <ul className={styles.stats}>
                    <li className={styles.stat}>
                        <p className={styles.statValue}>
                            Bottle {formatVolume(insights.bottleMlPerDay, unit)}
                        </p>
                        <p className={styles.meta}>
                            a day · {formatVolume(insights.bottleMl, unit)} total
                        </p>
                        {series.length > 0 ? (
                            <div className={styles.trendChart}>
                                <Suspense fallback={null}>
                                    <TrendChart
                                        days={series}
                                        childId={childId}
                                        metric="bottleMl"
                                        label="Bottle"
                                        formatValue={(value) => formatVolume(value, unit)}
                                    />
                                </Suspense>
                            </div>
                        ) : null}
                    </li>
                    <li className={styles.stat}>
                        <p className={styles.statValue}>
                            Breast {formatDuration(insights.breastSecondsPerDay)}
                        </p>
                        <p className={styles.meta}>
                            a day · {formatDuration(insights.breastSeconds)} total
                        </p>
                        {series.length > 0 ? (
                            <div className={styles.trendChart}>
                                <Suspense fallback={null}>
                                    <TrendChart
                                        days={series}
                                        childId={childId}
                                        metric="breastSeconds"
                                        label="Breast"
                                        formatValue={formatDuration}
                                    />
                                </Suspense>
                            </div>
                        ) : null}
                    </li>
                    <li className={styles.stat}>
                        <p className={styles.statValue}>
                            Sleep {formatDuration(insights.sleepSecondsPerDay)}
                        </p>
                        <p className={styles.meta}>
                            a day · {formatDuration(insights.sleepSeconds)} total
                        </p>
                        {series.length > 0 ? (
                            <div className={styles.trendChart}>
                                <Suspense fallback={null}>
                                    <TrendChart
                                        days={series}
                                        childId={childId}
                                        metric="sleepSeconds"
                                        label="Sleep"
                                        formatValue={formatDuration}
                                    />
                                </Suspense>
                            </div>
                        ) : null}
                    </li>
                    <li className={styles.stat}>
                        <p className={styles.statValue}>{nappyHeadline(insights)}</p>
                        <p className={styles.meta}>a day · {insights.nappyCount} total</p>
                        {series.length > 0 ? (
                            <div className={styles.trendChart}>
                                <Suspense fallback={null}>
                                    <TrendChart
                                        days={series}
                                        childId={childId}
                                        metric="nappyCount"
                                        label="Nappies"
                                        formatValue={(value) =>
                                            value === 1 ? '1 nappy' : `${value} nappies`
                                        }
                                    />
                                </Suspense>
                            </div>
                        ) : null}
                    </li>
                </ul>
            )}
        </section>
    );
}

function nappyHeadline(insights: ChildInsights): string {
    if (insights.nappyCountPerDay === 0 && insights.nappyCount > 0) {
        return 'Under 1 nappy';
    }
    return insights.nappyCountPerDay === 1 ? '1 nappy' : `${insights.nappyCountPerDay} nappies`;
}
